import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, InputAdornment, Paper, Snackbar, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import OutilService from "../../../services/smq/OutilService";
import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";
import { ToolHint, ToolSectionHeader } from "./OutilShared";
import PartiesInteresseesDialog from "./PartiesInteresseesDialog";

const EMPTY_DIALOG = { mode: "create", open: false, row: null };
const cellTextSx = {
  display: "-webkit-box", fontSize: 10.5, lineHeight: 1.35, overflow: "hidden",
  overflowWrap: "anywhere", WebkitBoxOrient: "vertical", WebkitLineClamp: 3,
};
const displayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
};

const PartiesInteressees = ({ outilEntree, processus }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(EMPTY_DIALOG);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [validatingId, setValidatingId] = useState(null);
  const [notification, setNotification] = useState({ message: "", open: false, severity: "success" });
  const requestIdRef = useRef(0);
  const entryReady = Boolean(
    outilEntree?.outil_entree_id &&
    String(outilEntree.processus_id) === String(processus?.processus_id)
  );

  const notify = useCallback((message, severity = "success") => setNotification({ message, open: true, severity }), []);

  const loadRows = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!processus?.processus_id || !entryReady) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await OutilService.getParties(processus.processus_id);
      if (!Array.isArray(response?.data)) throw new Error("Une erreur s’est produite lors de la récupération des données.");
      if (requestId === requestIdRef.current) setRows(response.data);
    } catch {
      if (requestId === requestIdRef.current) {
        setRows([]);
        setError("Une erreur s’est produite lors de la récupération des données.");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [entryReady, processus?.processus_id]);

  useEffect(() => {
    setSearch("");
    setDialog(EMPTY_DIALOG);
    loadRows();
    return () => { requestIdRef.current += 1; };
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr");
    if (!query) return rows;
    return rows.filter((row) => [
      row.nom_partie, row.attente, row.risque, row.opportunite, row.objectif, row.action,
      row.deroulement, row.processus, row.suivi_oi3, row.statut,
    ].some((value) => String(value || "").toLocaleLowerCase("fr").includes(query)));
  }, [rows, search]);

  const handleSaved = async (message) => {
    setDialog(EMPTY_DIALOG);
    notify(message);
    await loadRows();
  };

  const validateRow = async (row) => {
    if (row.statut === "Validé" || validatingId) return;
    setValidatingId(row.partie_interessee_id);
    try {
      const response = await OutilService.validatePartie(row.partie_interessee_id);
      notify(response.message || "Partie intéressée validée.");
      await loadRows();
    } catch (validationError) {
      notify(validationError.message, "error");
    } finally {
      setValidatingId(null);
    }
  };

  const deleteRow = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const response = await OutilService.deletePartie(deleteTarget.partie_interessee_id);
      setDeleteTarget(null);
      notify(response.message || "Partie intéressée supprimée.");
      await loadRows();
    } catch (deleteError) {
      notify(deleteError.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const openDialog = (mode, row = null) => setDialog({ mode, open: true, row });

  return (
    <Paper elevation={0} role="region" aria-label="Parties intéressées" sx={{ ...paperSx, overflow: "hidden" }}>
      <ToolSectionHeader
        addDisabled={!entryReady}
        addTooltip={!entryReady ? "Enregistrez d’abord la fiche documentaire" : "Ajouter une partie intéressée"}
        icon={<GroupsOutlinedIcon sx={{ fontSize: 15, verticalAlign: "middle" }} />}
        onAdd={() => openDialog("create")}
        title="Parties intéressées (PIP) — §4.2"
      />
      <ToolHint />
      {error && <Alert action={<Button color="inherit" onClick={loadRows}>Réessayer</Button>} severity="error" sx={{ mx: 1.5, my: 1 }}>{error}</Alert>}
      <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", gap: 1, px: 1.5, py: 1 }}>
        <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
        <TextField InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} onChange={(event) => setSearch(event.target.value)} placeholder="Filtrer toutes les colonnes..." size="small" sx={{ flex: 1, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 11, height: 34 } }} value={search} />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} entrée(s)</Typography>
        <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label="Parties intéressées" size="small" sx={{ minWidth: 1580, tableLayout: "fixed" }}>
          <TableHead><TableRow>{[
            ["Parties intéressées", 150], ["Attentes / Exigences", 180], ["Risques", 160],
            ["Opportunités", 160], ["Objectifs", 150], ["Actions", 170], ["Processus chargé", 140],
            ["Début", 95], ["Fin", 95], ["Suivi OI3", 120], ["Statut", 100], ["Actions", 135],
          ].map(([label, width], index) => <TableCell key={`${label}-${index}`} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell align="center" colSpan={12} sx={{ py: 3 }}><CircularProgress size={26} /></TableCell></TableRow>
              : filteredRows.length === 0 ? <TableRow><TableCell align="center" colSpan={12} sx={{ color: "#8091a3", fontSize: 11, py: 2.5 }}>Aucune entrée — cliquez sur Ajouter.</TableCell></TableRow>
                : filteredRows.map((row) => (
                  <TableRow hover key={row.partie_interessee_id} onClick={() => openDialog("view", row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f8fafc" }, "& td": { borderColor: "#e3eaf1", verticalAlign: "top" } }}>
                    <TableCell><Typography sx={{ ...cellTextSx, color: OUTIL_COLORS.navy, fontWeight: 800 }}>{row.nom_partie || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.attente || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.risque || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.opportunite || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.objectif || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.action || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.processus || "—"}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{displayDate(row.date_debut)}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{displayDate(row.date_fin)}</Typography></TableCell>
                    <TableCell><Typography sx={cellTextSx}>{row.suivi_oi3 || "—"}</Typography></TableCell>
                    <TableCell><Chip color={row.statut === "Validé" ? "success" : "warning"} label={row.statut || "Brouillon"} size="small" sx={{ fontSize: 9.5, fontWeight: 800 }} variant="outlined" /></TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title={row.statut === "Validé" ? "Déjà validé" : "Valider"}><span><IconButton aria-label="Valider" disabled={row.statut === "Validé" || validatingId === row.partie_interessee_id} onClick={() => validateRow(row)} size="small" sx={{ bgcolor: "#2e8b57", borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#237146" }, "&.Mui-disabled": { bgcolor: "#dce9e2", color: "#6f947e" } }}>{validatingId === row.partie_interessee_id ? <CircularProgress color="inherit" size={16} /> : <CheckIcon fontSize="small" />}</IconButton></span></Tooltip>
                        <Tooltip title="Modifier"><IconButton aria-label="Modifier" onClick={() => openDialog("edit", row)} size="small" sx={{ bgcolor: OUTIL_COLORS.navy, borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#0d2c49" } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Supprimer"><IconButton aria-label="Supprimer" onClick={() => setDeleteTarget(row)} size="small" sx={{ bgcolor: "#ad1f24", borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#8f171c" } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PartiesInteresseesDialog mode={dialog.mode} onClose={() => setDialog(EMPTY_DIALOG)} onSaved={handleSaved} open={dialog.open} outilEntree={outilEntree} processus={processus} row={dialog.row} />
      <Dialog onClose={() => !deleting && setDeleteTarget(null)} open={Boolean(deleteTarget)}>
        <DialogTitle>Supprimer la partie intéressée</DialogTitle>
        <DialogContent><DialogContentText>Confirmez-vous la suppression de « {deleteTarget?.nom_partie} » ? Sa pièce jointe physique sera conservée.</DialogContentText></DialogContent>
        <DialogActions><Button disabled={deleting} onClick={() => setDeleteTarget(null)}>Annuler</Button><Button color="error" disabled={deleting} onClick={deleteRow} variant="contained">{deleting ? <CircularProgress color="inherit" size={18} /> : "Supprimer"}</Button></DialogActions>
      </Dialog>
      <Snackbar anchorOrigin={{ horizontal: "right", vertical: "bottom" }} autoHideDuration={4500} onClose={() => setNotification((current) => ({ ...current, open: false }))} open={notification.open}>
        <Alert onClose={() => setNotification((current) => ({ ...current, open: false }))} severity={notification.severity} variant="filled">{notification.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default PartiesInteressees;
